import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Comment } from '../../types';
import { 
  getComments, 
  createComment, 
  updateComment, 
  deleteComment, 
  approveComment, 
  rejectComment,
  getCommentReplies
} from '../../api/comments';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import Button from './Button';
import { AiOutlineLock, AiOutlineUnlock, AiOutlineComment } from 'react-icons/ai';

interface CommentsSectionProps {
  postId: string;
  postAuthorId?: string;
  isCommentsLocked?: boolean;
  onCommentsLockedChange?: (locked: boolean) => void;
  className?: string;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  postId,
  postAuthorId,
  isCommentsLocked = false,
  onCommentsLockedChange,
  className = ''
}) => {
  const { user, token } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const isAdmin = user?.user_role === 'admin';
  const isPostAuthor = user?.id === postAuthorId;
  const canModerate = isAdmin || isPostAuthor;

  const fetchComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const fetchedComments = await getComments(postId);
      
      // Ensure we have an array to work with
      if (!fetchedComments || !Array.isArray(fetchedComments)) {
        console.warn('API returned invalid comments data:', fetchedComments);
        setComments([]);
        return;
      }
      
      // Build nested structure by grouping replies under parent comments
      const topLevelComments: Comment[] = [];
      
      // First pass: create top-level comments
      fetchedComments.forEach(comment => {
        if (!comment.parent_id) {
          // This is a top-level comment
          topLevelComments.push({
            ...comment,
            replies: [],
            reply_count: comment.reply_count || 0,
            hasMoreReplies: false,
            repliesOffset: 0,
            repliesLimit: 50
          });
        }
      });
      
      // Recursive function to fetch replies for a comment up to a certain depth
      const fetchRepliesRecursively = async (comment: Comment, depth: number = 0, maxFetchDepth: number = 5): Promise<Comment> => {
        // Only fetch replies if we're within the max fetch depth
        if (depth >= maxFetchDepth) {
          // Mark that there might be more replies available if reply_count > 0
          return {
            ...comment,
            replies: [],
            reply_count: comment.reply_count || 0,
            hasMoreReplies: (comment.reply_count || 0) > 0,
            repliesOffset: 0,
            repliesLimit: 50
          };
        }
        
        try {
          const replies = await getCommentReplies(comment.id, comment.repliesLimit || 50, comment.repliesOffset || 0);
          
          // Ensure we have an array to work with
          if (!replies || !Array.isArray(replies)) {
            console.warn(`API returned invalid replies data for comment ${comment.id}:`, replies);
            return {
              ...comment,
              replies: [],
              reply_count: comment.reply_count || 0,
              hasMoreReplies: (comment.reply_count || 0) > 0,
              repliesOffset: 0,
              repliesLimit: 50
            };
          }
          
          // Recursively fetch replies for each reply
          const repliesWithNestedReplies = await Promise.all(
            replies.map(async (reply) => {
              return await fetchRepliesRecursively({
                ...reply,
                replies: [],
                reply_count: reply.reply_count || 0,
                hasMoreReplies: false,
                repliesOffset: 0,
                repliesLimit: 50
              }, depth + 1, maxFetchDepth);
            })
          );
          
          // Check if there are more replies available beyond what we fetched
          const totalRepliesFetched = (comment.repliesOffset || 0) + repliesWithNestedReplies.length;
          const hasMoreReplies = (comment.reply_count || 0) > totalRepliesFetched;
          
          return {
            ...comment,
            replies: repliesWithNestedReplies,
            reply_count: comment.reply_count || 0,
            hasMoreReplies: hasMoreReplies,
            repliesOffset: totalRepliesFetched,
            repliesLimit: comment.repliesLimit || 50
          };
        } catch (error) {
          console.error(`Failed to fetch replies for comment ${comment.id}:`, error);
          return {
            ...comment,
            replies: [],
            reply_count: comment.reply_count || 0,
            hasMoreReplies: (comment.reply_count || 0) > 0,
            repliesOffset: 0,
            repliesLimit: 50
          };
        }
      };
      
      // Second pass: fetch replies for each comment recursively (up to 5 levels)
      const commentsWithReplies = await Promise.all(
        topLevelComments.map(async (comment) => {
          return await fetchRepliesRecursively(comment, 0, 5);
        })
      );
      
      setComments(commentsWithReplies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleCreateComment = async (content: string, parentId?: string) => {
    if (!token) return;
    
    try {
      const commentData = {
        content,
        ...(parentId && { parent_id: parentId })
      };
      
      const newComment = await createComment(postId, commentData, token);
      
      // Initialize the new comment with all required fields
      const initializedComment: Comment = {
        ...newComment,
        replies: [],
        reply_count: newComment.reply_count || 0,
        hasMoreReplies: false,
        repliesOffset: 0,
        repliesLimit: 50
      };
      
      if (parentId) {
        // Add reply to parent comment (could be a top-level comment or another reply)
        setComments(prev => {
          const updateCommentWithReply = (comments: Comment[]): Comment[] => {
            return comments.map(comment => {
              if (comment.id === parentId) {
                // This is the direct parent
                return {
                  ...comment,
                  replies: [...(comment.replies || []), initializedComment],
                  reply_count: (comment.reply_count || 0) + 1
                };
              }
              // Check if the parent is in the replies of this comment
              if (comment.replies) {
                return {
                  ...comment,
                  replies: updateCommentWithReply(comment.replies)
                };
              }
              return comment;
            });
          };
          
          return updateCommentWithReply(prev);
        });
      } else {
        // Add top-level comment
        setComments(prev => [initializedComment, ...prev]);
      }
      
      setReplyingTo(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create comment');
    }
  };

  const handleEditComment = async (commentId: string, content: string) => {
    if (!token) return;
    
    try {
      const updatedComment = await updateComment(commentId, { content }, token);
      
      setComments(prev => {
        const updateCommentInTree = (comments: Comment[]): Comment[] => {
          return comments.map(comment => {
            if (comment.id === commentId) {
              return { ...comment, ...updatedComment };
            }
            // Check if the comment is in the replies of this comment
            if (comment.replies) {
              return {
                ...comment,
                replies: updateCommentInTree(comment.replies)
              };
            }
            return comment;
          });
        };
        
        return updateCommentInTree(prev);
      });
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!token) return;
    
    try {
      await deleteComment(commentId, token);
      
      setComments(prev => {
        const removeCommentFromTree = (comments: Comment[]): Comment[] => {
          return comments.map(comment => {
            if (comment.id === commentId) {
              return null; // Remove this comment
            }
            // Check if the comment is in the replies of this comment
            if (comment.replies) {
              const updatedReplies = removeCommentFromTree(comment.replies);
              return {
                ...comment,
                replies: updatedReplies,
                reply_count: updatedReplies.length
              };
            }
            return comment;
          }).filter(Boolean) as Comment[];
        };
        
        return removeCommentFromTree(prev);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };

  const handleApproveComment = async (commentId: string) => {
    if (!token || !canModerate) return;
    
    try {
      const approvedComment = await approveComment(commentId, token);
      
      setComments(prev => {
        const updateCommentInTree = (comments: Comment[]): Comment[] => {
          return comments.map(comment => {
            if (comment.id === commentId) {
              return { ...comment, ...approvedComment };
            }
            // Check if the comment is in the replies of this comment
            if (comment.replies) {
              return {
                ...comment,
                replies: updateCommentInTree(comment.replies)
              };
            }
            return comment;
          });
        };
        
        return updateCommentInTree(prev);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve comment');
    }
  };

  const handleRejectComment = async (commentId: string, reason: string) => {
    if (!token || !canModerate) return;
    
    try {
      const rejectedComment = await rejectComment(commentId, { reason }, token);
      
      setComments(prev => {
        const updateCommentInTree = (comments: Comment[]): Comment[] => {
          return comments.map(comment => {
            if (comment.id === commentId) {
              return { ...comment, ...rejectedComment };
            }
            // Check if the comment is in the replies of this comment
            if (comment.replies) {
              return {
                ...comment,
                replies: updateCommentInTree(comment.replies)
              };
            }
            return comment;
          });
        };
        
        return updateCommentInTree(prev);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject comment');
    }
  };

  const handleReply = (commentId: string) => {
    setReplyingTo(commentId);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  // Function to fetch more replies for a specific comment
  const handleFetchMoreReplies = async (commentId: string) => {
    try {
      // Find the comment to get its current pagination state
      const findComment = (comments: Comment[]): Comment | null => {
        for (const comment of comments) {
          if (comment.id === commentId) {
            return comment;
          }
          if (comment.replies) {
            const found = findComment(comment.replies);
            if (found) return found;
          }
        }
        return null;
      };
      
      const currentComment = findComment(comments);
      if (!currentComment) {
        throw new Error('Comment not found');
      }
      
      const currentOffset = currentComment.repliesOffset || 0;
      const limit = currentComment.repliesLimit || 50;
      const newOffset = currentOffset + limit;
      
      // Fetch more replies
      const newReplies = await getCommentReplies(commentId, limit, newOffset);
      
      // Ensure we have an array to work with
      if (!newReplies || !Array.isArray(newReplies)) {
        console.warn(`API returned invalid replies data when fetching more replies for comment ${commentId}:`, newReplies);
        throw new Error('Invalid replies data received from API');
      }
      
      // Recursively fetch replies for each new reply (up to 5 levels from this point)
      const fetchRepliesRecursively = async (comment: Comment, depth: number = 0, maxFetchDepth: number = 5): Promise<Comment> => {
        if (depth >= maxFetchDepth) {
          return {
            ...comment,
            replies: [],
            reply_count: comment.reply_count || 0,
            hasMoreReplies: (comment.reply_count || 0) > 0,
            repliesOffset: 0,
            repliesLimit: 50
          };
        }
        
        try {
          const nestedReplies = await getCommentReplies(comment.id, comment.repliesLimit || 50, comment.repliesOffset || 0);
          const repliesWithNestedReplies = await Promise.all(
            nestedReplies.map(async (reply) => {
              return await fetchRepliesRecursively({
                ...reply,
                replies: [],
                reply_count: reply.reply_count || 0,
                hasMoreReplies: false,
                repliesOffset: 0,
                repliesLimit: 50
              }, depth + 1, maxFetchDepth);
            })
          );
          
          const totalRepliesFetched = (comment.repliesOffset || 0) + repliesWithNestedReplies.length;
          const hasMoreReplies = (comment.reply_count || 0) > totalRepliesFetched;
          
          return {
            ...comment,
            replies: repliesWithNestedReplies,
            reply_count: comment.reply_count || 0,
            hasMoreReplies: hasMoreReplies,
            repliesOffset: totalRepliesFetched,
            repliesLimit: comment.repliesLimit || 50
          };
        } catch (error) {
          console.error(`Failed to fetch replies for comment ${comment.id}:`, error);
          return {
            ...comment,
            replies: [],
            reply_count: comment.reply_count || 0,
            hasMoreReplies: (comment.reply_count || 0) > 0,
            repliesOffset: 0,
            repliesLimit: 50
          };
        }
      };
      
      const newRepliesWithNestedReplies = await Promise.all(
        newReplies.map(async (reply) => {
          return await fetchRepliesRecursively({
            ...reply,
            replies: [],
            reply_count: reply.reply_count || 0,
            hasMoreReplies: false,
            repliesOffset: 0,
            repliesLimit: 50
          }, 0, 5);
        })
      );
      
      // Update the comment with the new replies
      setComments(prev => {
        const updateCommentWithReplies = (comments: Comment[]): Comment[] => {
          return comments.map(comment => {
            if (comment.id === commentId) {
              const updatedReplies = [...(comment.replies || []), ...newRepliesWithNestedReplies];
              const totalRepliesFetched = newOffset + newRepliesWithNestedReplies.length;
              const hasMoreReplies = (comment.reply_count || 0) > totalRepliesFetched;
              
              return {
                ...comment,
                replies: updatedReplies,
                hasMoreReplies: hasMoreReplies,
                repliesOffset: totalRepliesFetched,
                repliesLimit: comment.repliesLimit || 50
              };
            }
            if (comment.replies) {
              return {
                ...comment,
                replies: updateCommentWithReplies(comment.replies)
              };
            }
            return comment;
          });
        };
        
        return updateCommentWithReplies(prev);
      });
    } catch (error) {
      console.error(`Failed to fetch more replies for comment ${commentId}:`, error);
      setError('Failed to load more replies');
    }
  };

  // Filter comments by status, but keep replies with their parent comments
  const approvedComments = comments.filter(comment => comment.status === 'approved');
  const pendingComments = comments.filter(comment => comment.status === 'pending');

  return (
    <div className={`space-y-6 text-[#4f453e] ${className}`}>
      {/* Section Header */}
      <div className="flex items-center justify-between gap-4 border-b border-[#8f7e69]/15 pb-4">
        <h3 className="flex items-center font-serif text-2xl text-[#2c201b]">
          <AiOutlineComment className="mr-2" />
          Comments ({approvedComments.length})
        </h3>
        
        {canModerate && onCommentsLockedChange && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => onCommentsLockedChange(!isCommentsLocked)}
          >
            {isCommentsLocked ? (
              <>
                <AiOutlineUnlock className="w-3 h-3 mr-1" />
                Unlock Comments
              </>
            ) : (
              <>
                <AiOutlineLock className="w-3 h-3 mr-1" />
                Lock Comments
              </>
            )}
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50/80 p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Comment Form */}
      {!isCommentsLocked && (
        <CommentForm
          onSubmit={(content) => handleCreateComment(content)}
          placeholder="Share your thoughts..."
        />
      )}

      {/* Comments Locked Message */}
      {isCommentsLocked && (
        <div className="rounded-xl border border-yellow-200 bg-yellow-50/80 p-4">
          <p className="text-yellow-800 text-center">
            Comments are locked for this post.
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="py-8 text-center">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#5a465f] border-t-transparent"></div>
          <p className="mt-2 text-[#6a5a4f]">Loading comments...</p>
        </div>
      )}

      {/* No Comments */}
      {!loading && approvedComments.length === 0 && (
        <div className="text-center py-8">
          <p className="text-[#6a5a4f]">No comments yet. Be the first to share your thoughts!</p>
        </div>
      )}

      {/* Approved Comments */}
      {!loading && approvedComments.length > 0 && (
        <div className="space-y-4">
          {approvedComments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onReply={handleReply}
              onEdit={handleEditComment}
              onDelete={handleDeleteComment}
              onApprove={canModerate ? handleApproveComment : undefined}
              onReject={canModerate ? handleRejectComment : undefined}
              isReplying={replyingTo === comment.id}
              replyingToId={replyingTo}
              onCancelReply={handleCancelReply}
              onSubmitReply={async (content: string) => {
                if (replyingTo) {
                  return handleCreateComment(content, replyingTo);
                } else {
                  return handleCreateComment(content, comment.id);
                }
              }}
              onFetchMoreReplies={handleFetchMoreReplies}
            />
          ))}
        </div>
      )}

      {/* Pending Comments (Admin/Author Only) */}
      {canModerate && pendingComments.length > 0 && (
        <div className="mt-8">
          <h4 className="mb-4 font-serif text-xl text-[#2c201b]">
            Pending Comments ({pendingComments.length})
          </h4>
          <div className="space-y-4">
            {pendingComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReply={handleReply}
                onEdit={handleEditComment}
                onDelete={handleDeleteComment}
                onApprove={handleApproveComment}
                onReject={handleRejectComment}
                isReplying={replyingTo === comment.id}
                replyingToId={replyingTo}
                onCancelReply={handleCancelReply}
                onSubmitReply={async (content: string) => {
                  if (replyingTo) {
                    return handleCreateComment(content, replyingTo);
                  } else {
                    return handleCreateComment(content, comment.id);
                  }
                }}
                onFetchMoreReplies={handleFetchMoreReplies}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
